import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LangContext';
import { countries } from '../data/countries';
import CountryChangeModal from '../components/CountryChangeModal';
import SafeIcon from '../common/SafeIcon';
import { FiUser, FiShield, FiClock } from 'react-icons/fi';
import { cn } from '../utils/cn';
import {
  changeLockedCountry,
  fetchUserProfile,
  fetchUserVoterIdentity,
  fetchUserVoteHistory,
  parseProfileRpcError,
} from '../services/profile';
import { cacheLockedCountries } from '../services/votes';

function countryName(code) {
  if (!code) return code;
  return countries.find((c) => c.code === code)?.name ?? code;
}

function countryLabel(code) {
  if (!code) return null;
  const name = countryName(code);
  return name === code ? code : `${name} (${code})`;
}

function formatVoteDate(iso, lang) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(lang === 'es' ? 'es' : 'en', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export default function Profile() {
  const { user, loading, syncLockedCountriesFromVote } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState('');
  const [lockedCountry1, setLockedCountry1] = useState(null);
  const [lockedCountry2, setLockedCountry2] = useState(null);
  const [voteHistory, setVoteHistory] = useState([]);
  const [showInvalidated, setShowInvalidated] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [changingCountry, setChangingCountry] = useState(null);

  const loadProfile = useCallback(async () => {
    if (!user) return;
    setPageLoading(true);
    try {
      const [profile, identity, history] = await Promise.all([
        fetchUserProfile(user.id),
        fetchUserVoterIdentity(),
        fetchUserVoteHistory(),
      ]);
      setDisplayName(profile.display_name || user.email);
      setLockedCountry1(identity.locked_country_1 ?? null);
      setLockedCountry2(identity.locked_country_2 ?? null);
      setVoteHistory(history);
      syncLockedCountriesFromVote(identity.locked_country_1, identity.locked_country_2);
      cacheLockedCountries(identity.locked_country_1, identity.locked_country_2);
    } catch (err) {
      console.error('Profile load failed:', err?.message ?? err, err);
      toast.error(t('profile_load_error'));
    } finally {
      setPageLoading(false);
    }
  }, [user, syncLockedCountriesFromVote, t]);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [loading, user, navigate]);

  useEffect(() => {
    if (user) {
      loadProfile();
    }
  }, [user, loadProfile]);

  const handleCountryChange = async (newCountry) => {
    if (!changingCountry) return false;
    try {
      const result = await changeLockedCountry(changingCountry, newCountry);
      setLockedCountry1(result.locked_country_1 ?? null);
      setLockedCountry2(result.locked_country_2 ?? null);
      syncLockedCountriesFromVote(result.locked_country_1, result.locked_country_2);
      cacheLockedCountries(result.locked_country_1, result.locked_country_2);

      const oldName = countries.find((c) => c.code === changingCountry)?.name ?? changingCountry;
      const newName = countries.find((c) => c.code === newCountry)?.name ?? newCountry;
      toast.success(t('profile_change_success', oldName, newName));
      setChangingCountry(null);
      const history = await fetchUserVoteHistory();
      setVoteHistory(history);
      return true;
    } catch (err) {
      const code = parseProfileRpcError(err);
      const messageKey = {
        duplicate_country: 'profile_error_duplicate',
        old_country_mismatch: 'profile_error_mismatch',
        identity_not_found: 'profile_error_no_identity',
        invalid_country: 'profile_error_invalid',
        not_authenticated: 'auth_required',
      }[code] ?? 'profile_change_error';
      toast.error(t(messageKey));
      console.error('change_locked_country failed:', err?.message ?? err, err);
      return false;
    }
  };

  if (loading || pageLoading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center text-slate-400">
        {t('loading')}
      </div>
    );
  }

  if (!user) return null;

  const lockedSlots = [
    { key: 'locked_country_1', code: lockedCountry1 },
    { key: 'locked_country_2', code: lockedCountry2 },
  ].filter((slot) => slot.code);

  const otherLockedCode = changingCountry === lockedCountry1 ? lockedCountry2 : lockedCountry1;

  const activeVotes = voteHistory.filter((v) => !v.invalidated_at);
  const invalidatedVotes = voteHistory.filter((v) => v.invalidated_at);
  const visibleVotes = showInvalidated ? voteHistory : activeVotes;
  const hasInvalidated = invalidatedVotes.length > 0;

  return (
    <div className="max-w-2xl mx-auto px-4 py-10">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-2xl">
        <div className="p-6 border-b border-slate-800">
          <h1 className="text-2xl font-black uppercase italic tracking-tighter flex items-center gap-2">
            <SafeIcon icon={FiUser} className="text-lime-400" />
            {t('profile_title')}
          </h1>
        </div>

        <div className="p-6 space-y-8">
          <div>
            <p className="text-[10px] font-black uppercase text-slate-500 tracking-wider mb-1">
              {t('profile_display_name')}
            </p>
            <p className="text-lg font-bold text-white">{displayName}</p>
            <p className="text-sm text-slate-500 mt-1">{user.email}</p>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-4">
              <SafeIcon icon={FiShield} className="text-lime-400" />
              <h2 className="text-sm font-black uppercase text-slate-400 tracking-wider">
                {t('profile_locked_countries')}
              </h2>
            </div>

            {lockedSlots.length === 0 ? (
              <p className="text-sm text-slate-500">{t('profile_no_identity')}</p>
            ) : (
              <ul className="space-y-3">
                {lockedSlots.map((slot) => (
                  <li
                    key={slot.key}
                    className="flex items-center justify-between gap-4 p-4 bg-slate-950 border border-slate-800 rounded-xl"
                  >
                    <span className="font-bold text-white">{countryLabel(slot.code)}</span>
                    <button
                      type="button"
                      onClick={() => setChangingCountry(slot.code)}
                      className="px-4 py-2 text-xs font-black uppercase tracking-wider bg-slate-800 hover:bg-slate-700 text-lime-400 rounded-lg transition-colors"
                    >
                      {t('change')}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <p className="text-xs text-slate-500 mt-4 leading-relaxed">
              {t('profile_change_hint')}
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between gap-4 mb-4">
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiClock} className="text-lime-400" />
                <h2 className="text-sm font-black uppercase text-slate-400 tracking-wider">
                  {t('profile_vote_history')}
                </h2>
              </div>
              {hasInvalidated && (
                <button
                  type="button"
                  onClick={() => setShowInvalidated((prev) => !prev)}
                  className="text-[10px] font-black uppercase tracking-wider text-slate-500 hover:text-lime-400 transition-colors"
                >
                  {showInvalidated ? t('profile_hide_invalidated') : t('profile_show_invalidated')}
                </button>
              )}
            </div>

            {visibleVotes.length === 0 ? (
              <p className="text-sm text-slate-500">{t('profile_no_votes')}</p>
            ) : (
              <ul className="space-y-3">
                {visibleVotes.map((vote, index) => {
                  const isInvalidated = Boolean(vote.invalidated_at);
                  const voteKey = `${vote.created_at}-${vote.voting_map_title}-${vote.voter_country}-${index}`;
                  return (
                    <li
                      key={voteKey}
                      className={cn(
                        'p-4 rounded-xl border',
                        isInvalidated
                          ? 'bg-slate-950/50 border-slate-800/60 opacity-60'
                          : 'bg-slate-950 border-slate-800'
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className={cn('min-w-0', isInvalidated && 'line-through decoration-slate-600')}>
                          <p className="font-bold text-white truncate">{vote.voting_map_title}</p>
                          <p className="text-sm text-slate-400 mt-1">
                            {t('profile_voted_as', countryName(vote.voter_country))}
                          </p>
                          <p className="text-sm text-lime-400/90 mt-0.5 font-semibold">
                            {t('profile_voted_for', vote.option_label ?? vote.option_choice?.toUpperCase())}
                          </p>
                        </div>
                        <div className="shrink-0 text-right">
                          {isInvalidated && (
                            <span className="inline-block text-[10px] font-black uppercase tracking-wider text-slate-500 bg-slate-900 px-2 py-0.5 rounded mb-1">
                              {t('profile_invalidated')}
                            </span>
                          )}
                          <p className="text-[10px] font-bold text-slate-500 uppercase">
                            {formatVoteDate(vote.created_at, lang)}
                          </p>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      <CountryChangeModal
        isOpen={Boolean(changingCountry)}
        onClose={() => setChangingCountry(null)}
        onConfirm={handleCountryChange}
        replacingCountry={changingCountry}
        excludedCodes={otherLockedCode ? [otherLockedCode] : []}
      />
    </div>
  );
}
